/**
 * A super admin's own shops, given the Shopify domain the server search found
 * for them so a search by domain still matches them locally; and the other
 * shops found, listed apart (BAB-3035).
 */
export function splitFoundShops<
  TShop extends { id: string; shopifyUrl?: string | null },
>(own: TShop[], found: TShop[]) {
  const foundById = new Map(found.map((shop) => [shop.id, shop]));
  return {
    ownShops: own.map((shop) => {
      const match = foundById.get(shop.id);
      return match ? { ...shop, shopifyUrl: match.shopifyUrl } : shop;
    }),
    otherShops: found.filter(
      (shop) => !own.some((ownShop) => ownShop.id === shop.id),
    ),
  };
}

/**
 * "Dashboard" for a super admin: their own shops, then the ones they pinned
 * (BAB-3035); the pinned ones leave the other shops. For anyone else, without
 * bookmarks, their own shops as before.
 */
export function withBookmarkedShops<
  TShop extends { id: string },
  TBookmark extends { id: string },
>(
  { ownShops, otherShops }: { ownShops: TShop[]; otherShops: TShop[] },
  bookmarkedShops: TBookmark[],
) {
  const ownIds = new Set(ownShops.map(({ id }) => id));
  const bookmarkedIds = new Set(bookmarkedShops.map(({ id }) => id));

  return {
    dashboardShops: [
      ...ownShops,
      ...bookmarkedShops.filter(({ id }) => !ownIds.has(id)),
    ],
    otherShops: otherShops.filter(({ id }) => !bookmarkedIds.has(id)),
    isOwnShop: (shopId: string) => ownIds.has(shopId),
    isBookmarkedShop: (shopId: string) => bookmarkedIds.has(shopId),
  };
}
